import { EyeOutlined, DownloadOutlined, WarningOutlined, InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Col, Divider, Row, Table, Empty, Radio, Form, Input, Popover, Modal, Button } from "antd"
import { useState, useContext, useEffect } from "react";
import { ModalVisualizarDocumentos } from "../../../../components/Generales/ModalVisualizarDocumentos";
import { PostulacionModel } from "../../../../models/PostulacionesModel/postulacion.model";
import { TemplateContext } from "../../../../utils/template_context";
import { DownloadFile } from "../../../../utils/DownloadFile";
import getStorageUser from "../../../../middlewares/GetStorageUser";
import "../../../Jurados/DocumentosAdministrativosJurado/documentacionAdministrativa.style.scss";
import apiDocumentos from "../../../../api/Documentos/api-documentos";
import jsPDF from 'jspdf';
import { PDF_PW, PDF_PH, PDF_MG, PDF_CW } from '../../../../utils/pdfPage';

// Lectura de la firma como data URL, fuera del componente para no anidar callbacks dentro del
// recorrido de documentos de la exportación.
const leerComoDataURL = (archivo: Blob): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(archivo);
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => resolve('');
  });

const firmaEnBase64 = async (ruta: string): Promise<string> => {
  if (!ruta) return '';
  try {
    const data = await apiDocumentos.consultarArchivo(ruta);
    if (data?.data) return await leerComoDataURL(data.data);
  } catch { /* firma no disponible */ }
  return '';
};

// Declaración juramentada de residencia del jurado, en PDF. Está fuera del componente para no
// anidar la generación dentro del recorrido de documentos de la exportación.
const construirDeclaracionResidenciaBlob = (
  datos: any,
  jurado: any,
  documentoParticipante: any,
  firmaB64: string
): Blob => {
  const PW = PDF_PW, PH = PDF_PH, LMARG = PDF_MG, CW = PDF_CW;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const str = (v: any) => (v == null ? '' : String(v));
  const LH = 5;
  const checkPg = (yy: number, needed: number): number => {
    if (yy + needed > PH - LMARG) { pdf.addPage(); return LMARG; }
    return yy;
  };
  let y = LMARG + 5;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('DECLARACIÓN JURAMENTADA DE RESIDENCIA', PW / 2, y, { align: 'center' });
  y += 10;
  const tipoPer = str(datos?.tipo_persona_declaracion);
  pdf.setFontSize(10);
  const op1 = `${tipoPer === 'colombiano' ? '(●)' : '(○)'} Persona natural: colombiano, residente en Colombia o fuera del país`;
  const op2 = `${tipoPer === 'extranjero' ? '(●)' : '(○)'} Persona natural: extranjero, con residencia en Colombia durante al menos los últimos dos (2) años`;
  pdf.setFont('helvetica', 'normal');
  const l1 = pdf.splitTextToSize(op1, CW);
  y = checkPg(y, l1.length * LH + 2); pdf.text(l1, LMARG, y); y += l1.length * LH + 3;
  const l2 = pdf.splitTextToSize(op2, CW);
  y = checkPg(y, l2.length * LH + 2); pdf.text(l2, LMARG, y); y += l2.length * LH + 8;
  const parrafo1 = ['Señores,', 'Convocatoria de Fomento y Estímulos para el Arte y la Cultura', 'Secretaría de Cultura Ciudadana', 'Alcaldía de Medellín', 'Distrito de Ciencia, Tecnología e Innovación'];
  y = checkPg(y, parrafo1.length * LH + 5);
  parrafo1.forEach(line => { pdf.text(line, LMARG, y); y += LH; });
  y += 5;
  const nombreJurado = [jurado?.primer_nombre, jurado?.segundo_nombre, jurado?.primer_apellido, jurado?.segundo_apellido].filter(Boolean).join(' ');
  const p2 = `Yo, ${nombreJurado}, identificado(a) con ${str(jurado?.tipo_identificacion)} número ${str(documentoParticipante)}, manifiesto bajo gravedad de juramento que mi lugar de residencia es ${str(datos?.pais)} y lo ha sido durante los últimos ${str(datos?.anos_residencia)} años.`;
  const p2L = pdf.splitTextToSize(p2, CW);
  y = checkPg(y, p2L.length * LH + 5); pdf.text(p2L, LMARG, y); y += p2L.length * LH + 8;
  const campos: [string, string][] = [['Dirección:', str(datos?.direccion)], ['País:', str(datos?.pais)], ['Ciudad:', str(datos?.ciudad)]];
  if (str(datos?.ciudad).toUpperCase() === 'MEDELLÍN') campos.push(['Comuna:', str(datos?.comuna)]);
  campos.push(['Correo electrónico:', str(jurado?.correo_electronico)], ['Teléfono:', str(jurado?.telefono_celular)]);
  for (const [lbl, val] of campos) {
    y = checkPg(y, LH + 2);
    pdf.setFont('helvetica', 'bold'); pdf.text(lbl, LMARG, y);
    pdf.setFont('helvetica', 'normal'); pdf.text(val, LMARG + pdf.getTextWidth(lbl + ' '), y);
    y += LH + 1;
  }
  y += 6;
  const ftL = pdf.splitTextToSize(`En constancia, se firma a los ${str(datos?.fecha_firma)}`, CW);
  y = checkPg(y, ftL.length * LH + 25); pdf.text(ftL, LMARG, y); y += ftL.length * LH + 3;
  if (firmaB64) {
    try { pdf.addImage(firmaB64, firmaB64.startsWith('data:image/jpe') ? 'JPEG' : 'PNG', LMARG, y, 50, 20); } catch { /* firma no disponible */ }
  }
  return pdf.output('blob');
};

export const PerfilJurado = ({ juradoPostulacion, jurado, areaPrincipal, registerExportFn }: any) => {
  const [openModal, setOpenModal] = useState(false);
  const [documento, setDocumento] = useState<any>({});
  const [radioValues, setRadioValues] = useState([]);
  const [user, setUser] = useState<any>({});
  const [openModalDeclaracion, setOpenModalDeclaracion] = useState(false);
  const [datosDeclaracion, setDatosDeclaracion] = useState<any>({});

  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = useState<string | null>(null);

  const getUser = async () => {
    try {
      const _user = getStorageUser()
      setUser(_user)
    } catch (error) {
      console.log(error);
    }
  };

  const modalAnexo = (datos: any, documentoCompleto?: any) => {
    // Si el documento es "Declaración de Residencia" y tiene datos guardados, mostrar modal de declaración
    if (documentoCompleto?.descripcion === "Declaración de Residencia" || documentoCompleto?.tipo_documento === "Declaración de Residencia") {
      setDatosDeclaracion(documentoCompleto);
      setOpenModalDeclaracion(true);
      getSignature(documentoCompleto?.firma_declaracion || documentoCompleto?.url_participante)
    } else {
      setDocumento(datos)
      setOpenModal(true);
    }
  }

  const [fileName, setFileName] = useState<any>("");

  const getSignature = async (firma) => {

    const data = await apiDocumentos.consultarArchivo(firma);
    if (data?.data) {
      const reader = new FileReader();
      reader.readAsDataURL(data.data);
      reader.onloadend = function () {
        const base64String = reader.result;
        setFileName(base64String);
      }
    }
  };

  const generarDeclaracionResidenciaPDF = async (firmaBase64: string, datosDecl?: any): Promise<Blob> => {
    const dd = datosDecl ?? datosDeclaracion;
    const PW = PDF_PW, PH = PDF_PH, MG = PDF_MG, CW = PDF_CW;
    const pdf = new jsPDF('p', 'mm', 'a4');
    const str = (v: any) => (v == null ? '' : String(v));
    const LH = 5;

    const checkPage = (yy: number, needed: number): number => {
      if (yy + needed > PH - MG) { pdf.addPage(); return MG; }
      return yy;
    };

    let y = MG + 5;

    // Título
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(0, 0, 0);
    pdf.text('DECLARACIÓN JURAMENTADA DE RESIDENCIA', PW / 2, y, { align: 'center' });
    y += 10;

    // Tipo persona
    const tipoPer = str(dd?.tipo_persona_declaracion);
    pdf.setFontSize(10);
    const opcion1 = `${tipoPer === 'colombiano' ? '(●)' : '(○)'} Persona natural: colombiano, residente en Colombia o fuera del país`;
    const opcion2 = `${tipoPer === 'extranjero' ? '(●)' : '(○)'} Persona natural: extranjero, con residencia en Colombia durante al menos los últimos dos (2) años`;
    const l1 = pdf.splitTextToSize(opcion1, CW);
    y = checkPage(y, l1.length * LH + 2);
    pdf.setFont('helvetica', 'normal');
    pdf.text(l1, MG, y);
    y += l1.length * LH + 3;
    const l2 = pdf.splitTextToSize(opcion2, CW);
    y = checkPage(y, l2.length * LH + 2);
    pdf.text(l2, MG, y);
    y += l2.length * LH + 8;

    // Párrafo señores
    const parrafo1 = [
      'Señores,',
      'Convocatoria de Fomento y Estímulos para el Arte y la Cultura',
      'Secretaría de Cultura Ciudadana',
      'Alcaldía de Medellín',
      'Distrito de Ciencia, Tecnología e Innovación',
    ];
    y = checkPage(y, parrafo1.length * LH + 5);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    parrafo1.forEach(line => { pdf.text(line, MG, y); y += LH; });
    y += 5;

    // Párrafo declaración
    const nombreJurado = [jurado?.primer_nombre, jurado?.segundo_nombre, jurado?.primer_apellido, jurado?.segundo_apellido]
      .filter(Boolean).join(' ');
    const parrafo2 = `Yo, ${nombreJurado}, identificado(a) con ${str(jurado?.tipo_identificacion)} número ${str(juradoPostulacion?.data?.numero_documento_participante || '')}, manifiesto bajo gravedad de juramento que mi lugar de residencia es ${str(dd?.pais)} y lo ha sido durante los últimos ${str(dd?.anos_residencia)} años.`;
    const p2Lines = pdf.splitTextToSize(parrafo2, CW);
    y = checkPage(y, p2Lines.length * LH + 5);
    pdf.text(p2Lines, MG, y);
    y += p2Lines.length * LH + 8;

    // Campos
    const campos: [string, string][] = [
      ['Dirección:', str(dd?.direccion)],
      ['País:', str(dd?.pais)],
      ['Ciudad:', str(dd?.ciudad)],
    ];
    if (str(dd?.ciudad).toUpperCase() === 'MEDELLÍN') {
      campos.push(['Comuna:', str(dd?.comuna)]);
    }
    campos.push(
      ['Correo electrónico:', str(jurado?.correo_electronico)],
      ['Teléfono:', str(jurado?.telefono_celular)],
    );
    for (const [lbl, val] of campos) {
      y = checkPage(y, LH + 2);
      pdf.setFont('helvetica', 'bold');
      const lblW = pdf.getTextWidth(lbl + ' ');
      pdf.text(lbl, MG, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(val, MG + lblW, y);
      y += LH + 1;
    }
    y += 6;

    // Firma
    const firmaText = `En constancia, se firma a los ${str(dd?.fecha_firma)}`;
    const ftLines = pdf.splitTextToSize(firmaText, CW);
    y = checkPage(y, ftLines.length * LH + 25);
    pdf.setFont('helvetica', 'normal');
    pdf.text(ftLines, MG, y);
    y += ftLines.length * LH + 3;
    if (firmaBase64) {
      try {
        const fmt = firmaBase64.startsWith('data:image/jpe') ? 'JPEG' : 'PNG';
        pdf.addImage(firmaBase64, fmt, MG, y, 50, 20);
      } catch { /* firma no disponible */ }
    }

    return pdf.output('blob');
  };

  const imprimirPdf = async (nombreArchivo: string, datosDecl?: any, firmaB64?: string) => {
    const blob = await generarDeclaracionResidenciaPDF(firmaB64 ?? fileName, datosDecl);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${nombreArchivo}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadOnlinePdfBackground = async (datos: any) => {
    setLoadingDownload(datos.descripcion);
    setIsExporting(true);

    try {
      if (datos.descripcion === "Declaración de Residencia" || datos.tipo_documento === "Declaración de Residencia") {
        setDatosDeclaracion(datos);
        let firmaB64 = '';
        if (datos.firma_declaracion || datos.url_participante) {
          const firma = datos.firma_declaracion || datos.url_participante;
          try {
            const data = await apiDocumentos.consultarArchivo(firma);
            if (data?.data) {
              firmaB64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(data.data);
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = () => resolve('');
              });
            }
          } catch { /* firma no disponible */ }
        }
        await imprimirPdf(`Declaracion_Residencia_${juradoPostulacion.data?.radicado || ''}`, datos, firmaB64);
        setOpenModalDeclaracion(false);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsExporting(false);
      setLoadingDownload(null);
    }
  }



  const context = useContext(TemplateContext);

  const [observaciones, setObservaciones] = useState(juradoPostulacion.data?.observaciones || '');

  const handleObservacionesBlur = () => {
    if (observaciones !== '') {
      console.log('Observaciones guardadas:', observaciones);
      juradoPostulacion.data.observaciones = observaciones;
      console.log('Data actualizada:', juradoPostulacion.data);
    }
  };

  useEffect(() => {
    getUser()
  }, []);

  useEffect(() => {
    if (registerExportFn) {
      registerExportFn(async () => {
        const out: { nombre: string, blob: Blob }[] = [];
        let i = 1;
        for (const doc of (juradoPostulacion.data?.documentos_administrativos || [])) {
          if (doc.descripcion === "Declaración de Residencia" || doc.tipo_documento === "Declaración de Residencia") {
            setIsExporting(true);
            const firmaB64 = await firmaEnBase64(doc.firma_declaracion || doc.url_participante);

            const blob = construirDeclaracionResidenciaBlob({ ...doc }, jurado, juradoPostulacion?.data?.numero_documento_participante || '', firmaB64);
            out.push({ nombre: `administrativo/administrativos${i}.pdf`, blob });
            setIsExporting(false);
          }
          i++;
        }
        return out;
      });
    }
  }, [registerExportFn, juradoPostulacion]);

  const obtenerFecha = () => {
    let fechaActual = new Date();
    let anio = fechaActual.getFullYear();
    let mes = ('0' + (fechaActual.getMonth() + 1)).slice(-2);
    let dia = ('0' + fechaActual.getDate()).slice(-2);
    let hora = ('0' + fechaActual.getHours()).slice(-2);
    let minutos = ('0' + fechaActual.getMinutes()).slice(-2);
    let fechaYHora = anio + '-' + mes + '-' + dia + ' ' + hora + ':' + minutos;
    return fechaYHora
  }

  const handlechangecheck = (
    e: any,
    datos: any,
    index: number,
    postulacion: PostulacionModel
  ) => {
    if (juradoPostulacion.data.id_postulacion === postulacion.id_postulacion) {
      if (e.target.name === "checksubsanable") {
        juradoPostulacion.data.documentos_administrativos[index].checksubsanable = true;
        juradoPostulacion.data.documentos_administrativos[index].checkaceptar = false;
        juradoPostulacion.data.documentos_administrativos[index].checkcancelar = false;
        juradoPostulacion.data.documentos_administrativos[index].actualizado_por = `${user.nombres} ${user.apellidos}`
        juradoPostulacion.data.documentos_administrativos[index].fecha_subsanar = obtenerFecha()
      }
      if (e.target.name === "checkaceptar") {
        juradoPostulacion.data.documentos_administrativos[index].checksubsanable =
          false;
        juradoPostulacion.data.documentos_administrativos[index].checkaceptar = true;
        juradoPostulacion.data.documentos_administrativos[index].checkcancelar = false;
        juradoPostulacion.data.documentos_administrativos[index].aceptado_por = `${user.nombres} ${user.apellidos}`
        juradoPostulacion.data.documentos_administrativos[index].fecha_aceptado = obtenerFecha()
      }
      if (e.target.name === "checkrechazado") {
        juradoPostulacion.data.documentos_administrativos[index].checksubsanable =
          false;
        juradoPostulacion.data.documentos_administrativos[index].checkaceptar = false;
        juradoPostulacion.data.documentos_administrativos[index].checkcancelar = true;
        juradoPostulacion.data.documentos_administrativos[index].rechazado_por = `${user.nombres} ${user.apellidos}`
        juradoPostulacion.data.documentos_administrativos[index].fecha_rechazado = obtenerFecha()
      }
    }
    const newRadioValues = [...radioValues];
    newRadioValues[index] = e.target.value;
    setRadioValues(newRadioValues);
  };

  const columnasDocumentacionadministrativaModalUser: any = [
    {
      title: "No.",
      width: 40,
      render: (datos: any, datos2: any, index: number) => {
        return (
          <span>{index + 1}</span>
        );
      },
      fixed: context.device === "md" ? "left" : false,
    },
    {
      title: "Tipo de documento",
      width: 380,
      fixed: context.device === "md" ? "left" : false,
      render: (datos: any) => (
        <Row className="width-100">
          <Col span={24}>{datos.descripcion}</Col>
        </Row>
      ),
    },
    {
      title: (
        <Row className="width-100">
          <Col span={24} className="justify-content-center">
            Info doc
          </Col>
        </Row>
      ),
      width: 100,
      render: (datos: any, record: any, index: number) => {
        return (
            <Row className="width-100">
              <Col span={24} className="justify-content-center">
                <Popover
                  title="Trazabilidad del documento"
                  content={
                    <div style={{ fontSize: 'small' }}>
                      <p className="font-color-6435C9">Fecha subsanado: <span style={{ fontWeight: 'bold' }}>{datos.fecha_subsanado}</span></p>
                      <p className="font-color-6435C9">Subsanado por: <span style={{ fontWeight: 'bold' }}>{datos.subsanado_por}</span></p>
                      {datos.url_anterior && (
                        <p className="font-color-6435C9">Ver anterior: {' '}
                          <span style={{ fontWeight: 'bold' }}>
                            <EyeOutlined
                              onClick={() => {
                                setOpenModal(true)
                                setDocumento(datos.url_anterior);
                              }} />
                          </span>
                        </p>)
                      }
                      <hr />
                    </div>
                  }>
                  <InfoCircleOutlined />
                </Popover>
              </Col>
            </Row>
        );
      },
    },
    {
      title: (
        <>
          <Row className="width-100">
            <Col span={24} className="justify-content-center">
              Acciones
            </Col>
          </Row>
          <Row className="width-100">
            <Col span={12} className="justify-content-center">
              Ver
            </Col>
            <Col span={12} className="justify-content-center">
              Descargar
            </Col>
          </Row>
        </>
      ),
      width: 120,
      render: (datos: any, record: any, index: number) => {
        const esDeclaracion = datos?.descripcion === "Declaración de Residencia" || datos?.tipo_documento === "Declaración de Residencia";
        const tieneDocumento = datos?.url_participante || esDeclaracion;
        return (
          <Row className="width-100">
            <Col span={12} className="justify-content-center">
              {
                tieneDocumento &&
                <EyeOutlined
                  onClick={() => {
                    modalAnexo(datos.url_participante, datos)
                  }}
                />
              }
              {
                !tieneDocumento &&
                <WarningOutlined />
              }
            </Col>
            <Col span={12} className="justify-content-center">
              {(() => {
                if (!tieneDocumento) return <WarningOutlined />;
                if (loadingDownload === datos.descripcion) return <LoadingOutlined />;
                return <DownloadOutlined onClick={() => {
                  if (datos?.descripcion === "Declaración de Residencia" || datos?.tipo_documento === "Declaración de Residencia") {
                    downloadOnlinePdfBackground(datos);
                  } else if (datos.url_participante) {
                    DownloadFile(datos.url_participante);
                  }
                }} />;
              })()}
            </Col>
          </Row>
        );
      },
    },
  ]
  const columnasDocumentacionadministrativaModal: any = [
    {
      title: "No.",
      width: 40,
      render: (datos: any, datos2: any, index: number) => {
        return (
          <span>{index + 1}</span>
        );
      },
      fixed: context.device === "md" ? "left" : false,
    },
    {
      title: "Tipo de documento",
      width: 380,
      fixed: context.device === "md" ? "left" : false,
      render: (datos: any) => (
        <Row className="width-100">
          <Col span={24}>{datos.descripcion}</Col>
        </Row>
      ),
    },
    {
      title: (
        <Row className="width-100">
          <Col span={24} className="justify-content-center">
            Info doc
          </Col>
        </Row>
      ),
      width: 100,
      render: (datos: any, record: any, index: number) => {
        return (
            <Row className="width-100">
              <Col span={24} className="justify-content-center">
                <Popover
                  title="Trazabilidad del documento"
                  content={
                    <div style={{ fontSize: 'small' }}>
                      <p className="font-color-6435C9">Fecha subsanado: <span style={{ fontWeight: 'bold' }}>{datos.fecha_subsanado}</span></p>
                      <p className="font-color-6435C9">Subsanado por: <span style={{ fontWeight: 'bold' }}>{datos.subsanado_por}</span></p>
                      {datos.url_anterior && (
                        <p className="font-color-6435C9">Ver anterior: {' '}
                          <span style={{ fontWeight: 'bold' }}>
                            <EyeOutlined
                              onClick={() => {
                                setOpenModal(true)
                                setDocumento(datos.url_anterior);
                              }} />
                          </span>
                        </p>)
                      }
                      <hr />
                      <p className="font-color-F28C02">Fecha subsanar: <span style={{ fontWeight: 'bold' }}>{datos.fecha_subsanar}</span></p>
                      <p className="font-color-F28C02">Actualizado por: <span style={{ fontWeight: 'bold' }}>{datos.actualizado_por}</span></p>
                      <hr />
                      <p className="font-color-00904C">Fecha aceptado: <span style={{ fontWeight: 'bold' }}>{datos.fecha_aceptado}</span></p>
                      <p className="font-color-00904C">Aceptado por: <span style={{ fontWeight: 'bold' }}>{datos.aceptado_por}</span></p>
                      <hr />
                      <p className="font-color-DB2828">Fecha rechazado: <span style={{ fontWeight: 'bold' }}>{datos.fecha_rechazado}</span></p>
                      <p className="font-color-DB2828">Rechazado por: <span style={{ fontWeight: 'bold' }}>{datos.rechazado_por}</span></p>
                    </div>
                  }>
                  <InfoCircleOutlined />
                </Popover>
              </Col>
            </Row>
        );
      },
    },
    {
      title: (
        <Row className="width-100">
          <Col span={24} className="justify-content-center">
            Subsanar
          </Col>
        </Row>
      ),
      width: 100,
      render: (datos: any, record: any, index: number) => {
        return (
            <Row className="width-100">
              <Col span={24} className="justify-content-center">
                <Radio
                  name="checksubsanable"
                  value={datos.checksubsanable}
                  checked={datos.checksubsanable}
                  onChange={(e) =>
                    handlechangecheck(e, datos, index, juradoPostulacion.data)
                  }
                />
              </Col>
            </Row>
        );
      },
    },
    {
      title: (
        <Row className="width-100">
          <Col span={24} className="justify-content-center">
            Aceptado
          </Col>
        </Row>
      ),
      width: 100,
      render: (datos: any, record: any, index: number) => {
        return (
          <Row className="width-100">
            <Col span={24} className="justify-content-center">
              <Radio
                name="checkaceptar"
                checked={datos.checkaceptar}
                value={datos.checkaceptar}
                onChange={(e) =>
                  handlechangecheck(e, datos, index, juradoPostulacion.data)
                }
              />
            </Col>
          </Row>
        );
      },
    },
    {
      title: (
        <Row className="width-100">
          <Col span={24} className="justify-content-center">
            Rechazado
          </Col>
        </Row>
      ),
      width: 100,
      render: (datos: any, record: any, index: number) => {
        return (
          <Row className="width-100">
            <Col span={24} className="justify-content-center">
              <Radio
                name="checkrechazado"
                checked={datos.checkcancelar}
                value={datos.checkcancelar}
                onChange={(e) =>
                  handlechangecheck(e, datos, index, juradoPostulacion.data)
                }
              />
            </Col>
          </Row>
        );
      },
    },
    {
      title: (
        <>
          <Row className="width-100">
            <Col span={24} className="justify-content-center">
              Acciones
            </Col>
          </Row>
          <Row className="width-100">
            <Col span={12} className="justify-content-center">
              Ver
            </Col>
            <Col span={12} className="justify-content-center">
              Descargar
            </Col>
          </Row>
        </>
      ),
      width: 120,
      render: (datos: any, record: any, index: number) => {
        const esDeclaracion = datos?.descripcion === "Declaración de Residencia" || datos?.tipo_documento === "Declaración de Residencia";
        const tieneDocumento = datos?.url_participante || esDeclaracion;
        return (
          <Row className="width-100">
            <Col span={12} className="justify-content-center">
              {
                tieneDocumento &&
                <EyeOutlined
                  onClick={() => {
                    modalAnexo(datos.url_participante, datos)
                  }}
                />
              }
              {
                !tieneDocumento &&
                <WarningOutlined />
              }
            </Col>
            <Col span={12} className="justify-content-center">
              {(() => {
                if (!tieneDocumento) return <WarningOutlined />;
                if (loadingDownload === datos.descripcion) return <LoadingOutlined />;
                return <DownloadOutlined onClick={() => {
                  if (datos?.descripcion === "Declaración de Residencia" || datos?.tipo_documento === "Declaración de Residencia") {
                    downloadOnlinePdfBackground(datos);
                  } else if (datos.url_participante) {
                    DownloadFile(datos.url_participante);
                  }
                }} />;
              })()}
            </Col>
          </Row>
        );
      },
    },
  ];

  return (
    <Row gutter={[24, 24]} style={{ padding: '20px' }}>
      <Col span={24}>
        <div className="font-size-12px font-family-Montserrat-SemiBold">Descripción del perfil</div>
        <Divider style={{ margin: '2px' }} />
        <p className="font-family-Work-Sans font-size-12px font-color-535353" >{jurado.perfil}</p>
      </Col>
      <Col span={24}>
        <div className="font-size-12px font-family-Montserrat-SemiBold">Modalidad</div>
        <Divider style={{ margin: '2px' }} />
        <p className="font-family-Work-Sans font-size-12px font-color-535353">
          {(() => {
            const lastCat = juradoPostulacion.data.categorias_area?.[juradoPostulacion.data.categorias_area.length - 1];
            if (lastCat?.nombre) return lastCat.nombre;
            const tipo = jurado?.areas_conocimientos?.[0]?.tipo_conocimiento;
            if (tipo === 1) return "Experto con título";
            if (tipo === 2) return "Experto sin título";
            return "";
          })()}
        </p>
      </Col>
      <Col span={24}>
        <div className="font-size-12px font-family-Montserrat-SemiBold">Área principal de conocimiento</div>
        <Divider style={{ margin: '2px' }} />
        <p className="font-family-Work-Sans font-size-12px font-color-535353" >{juradoPostulacion.data.area}</p>
      </Col>
      {/*
            <Col span={24} >
                <div className="font-size-12px font-family-Montserrat-SemiBold">Categorías de conocimiento (SCC)</div>
                <Divider style={{margin: '2px'}} />
                <ul className="font-color-051923bf" style={{paddingLeft: '14px'}}>
                    {(juradoPostulacion.data.categorias_area || []).map((x: any) => (
                      <li className="font-family-Work-Sans font-size-12px font-color-051923bf" key={x.id}>
                        <span className="font-color-535353">{x.nombre}</span>
                      </li>
                    ))}
                </ul>
            </Col>
            */}
      <Col span={24}>
        <div className="font-size-12px font-family-Montserrat-SemiBold">Categorías de conocimiento</div>
        <Divider style={{ margin: '2px' }} />
        <ul className="font-color-051923bf" style={{ paddingLeft: '14px' }}>
          {!juradoPostulacion?.data?.areas_conocimientos?.[0]
            ? (juradoPostulacion.data.categorias_area || [])
              .filter((x: any) => x.nombre !== "Experto sin título" && x.nombre !== "Experto con título")
              .map((x: any) => (
                <li className="font-family-Work-Sans font-size-12px font-color-051923bf" key={x.id}>
                  <span className="font-color-535353">{x.nombre}</span>
                </li>
              ))
            : jurado.areas_conocimientos[0].areas_conocimientos.map((x: any) => (
              <li className="font-family-Work-Sans font-size-12px font-color-051923bf" key={x.id}>
                <span className="font-color-535353">{x.nombre}</span>
              </li>
            ))}
        </ul>
      </Col>

      {openModal &&
        <ModalVisualizarDocumentos
          visualizar={openModal}
          setVisualizar={setOpenModal}
          state={{ documento }}
        />
      }
      {openModalDeclaracion && (
        <Modal
          style={{ top: 20, zIndex: isExporting ? -999 : 3 }}
          wrapProps={{ style: isExporting ? { position: 'fixed', left: '-9999px' } : undefined }}
          mask={!isExporting}
          visible={openModalDeclaracion}
          footer={[
            <Button
              key="download"
              onClick={() => imprimirPdf(`Declaracion_Residencia_${juradoPostulacion.data?.radicado || ''}`)}
              className="button-blanco-radius"
            >
              Descargar PDF
            </Button>,
            <Button
              key="close"
              onClick={() => setOpenModalDeclaracion(false)}
              className="button-blanco-radius"
            >
              Cerrar
            </Button>
          ]}
          closable={true}
          onCancel={() => setOpenModalDeclaracion(false)}
          width={800}
          maskStyle={{
            zIndex: 3,
            background: "rgba(0,144,76,0.8)",
          }}
          className="modal-postulacion-exitosa"
        >
          <div className="document-wrapper">
            <div id="modal_custom_declaracion" className="document-page">
              <h3 className="text-center">
                DECLARACIÓN JURAMENTADA DE RESIDENCIA
              </h3>

              <div style={{ marginBottom: "20px" }}>
                <Radio.Group
                  value={datosDeclaracion?.tipo_persona_declaracion || ""}
                  style={{ width: "100%" }}
                  disabled
                >
                  <Radio value="colombiano" style={{ display: "block", marginBottom: "10px", whiteSpace: "normal" }}>
                    Persona natural: <strong>colombiano</strong>, residente en Colombia o fuera del país
                  </Radio>
                  <Radio value="extranjero" style={{ display: "block", whiteSpace: "normal" }}>
                    Persona natural: <strong>extranjero</strong>, con residencia en Colombia durante al menos los últimos dos (2) años
                  </Radio>
                </Radio.Group>
              </div>

              <p className="text-justify">
                Señores, <br />
                <strong>Convoctoria de Fomento y Estímulos para el Arte y la Cultura</strong> <br />
                Secretaría de Cultura Ciudadana <br />
                Alcaldía de Medellín <br />
                Distrito de Ciencia, Tecnología e Innovación
              </p>
              <br />
              <p className="text-justify">
                Yo, <strong>{jurado?.primer_nombre} {jurado?.segundo_nombre} {jurado?.primer_apellido} {jurado?.segundo_apellido}</strong>, identificado(a) con{" "}
                <strong> {jurado.tipo_identificacion}</strong> número{" "}
                <strong> {juradoPostulacion?.data?.numero_documento_participante || ""}</strong>, manifiesto bajo{" "}
                gravedad de juramento que mi lugar de residencia es{" "}
                <strong>{datosDeclaracion?.pais || ""} </strong>
                y lo ha sido durante los
                últimos{" "}
                <strong>{datosDeclaracion?.anos_residencia || ""}</strong>{" "}
                años.
              </p>
              <br />
              <p><strong>Dirección: {datosDeclaracion?.direccion || ""}</strong></p>
              <p><strong>País: {datosDeclaracion?.pais || ""}</strong></p>
              <p><strong>Ciudad: {datosDeclaracion?.ciudad || ""}</strong></p>
              {datosDeclaracion?.ciudad == "MEDELLÍN" && (
                <p><strong>Comuna: {datosDeclaracion?.comuna || ""}</strong></p>
              )}
              <p><strong>Correo electrónico: {jurado.correo_electronico}</strong> </p>
              <p><strong>Teléfono: {jurado.telefono_celular}</strong> </p>

              <br />

              <p className="text-right">
                En constancia, se firma a los {datosDeclaracion?.fecha_firma}
                <img
                  src={fileName}
                  style={{
                    height: "50%",
                    width: "50%",
                    borderTop: "none",
                  }}
              alt=""
              />
              </p>
              <br /><br />
            </div>
          </div>
        </Modal>
      )}
      {
        <Col span={24} >
          <div
            className="col-12 font-family-Montserrat-Regular font-size-13px"
            style={{ paddingTop: "16px", paddingBottom: "1%" }}
          >
            <span className="font-family-Montserrat-SemiBold">
              Documentos administrativos
            </span>
            <Table
              style={{ borderTop: "1px solid #C4C4C4" }}
              pagination={false}
              columns={user.role_id != 3 ? columnasDocumentacionadministrativaModal : columnasDocumentacionadministrativaModalUser}
              locale={{
                emptyText: (
                  <Empty
                    description="No se han diligenciados datos para está sección"
                    style={{ padding: "50px" }}
                  />
                ),
              }}
              dataSource={juradoPostulacion.data.documentos_administrativos == '{}' ? '' : juradoPostulacion.data.documentos_administrativos}
            />
          </div>
        </Col>
      }
      {
        user.role_id != 3 ?
          <Col
            span={24}
            className="font-family-Montserrat-Regular font-size-13px"
            style={{ paddingTop: "16px", paddingBottom: "1%" }}
          >
            <Form
              name="basic"
              layout="vertical"
              initialValues={{ remember: true }}
              autoComplete="off"
            >
              <Input.TextArea
                value={observaciones}
                rows={10}
                maxLength={2000}
                required
                onBlur={handleObservacionesBlur}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Ingresa las observaciones"
              />
            </Form>
          </Col>
          :
          <span>{observaciones}</span>
      }
    </Row>
  )
}
